import React from "react";
import { Button, Input, InputNumber, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import { generateOptionId } from "../gameTypes";
import TtsAudioControls from "../TtsAudioControls";
import OptionRichText from "../OptionRichText";

const stripHtml = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

type Props = {
  game: Extract<Game, { type: "fill_in_blanks" }>;
  onChange: (game: Extract<Game, { type: "fill_in_blanks" }>) => void;
  generateAudio?: (text: string) => Promise<string>;
  generateAudioWithBlanks?: (text: string) => Promise<string>;
};

const FillInBlanksEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
  generateAudioWithBlanks,
}) => {
  // Defensive: partial/legacy games may arrive without these arrays.
  // Fall back to empty lists so the map() calls below don't crash.
  const options: typeof game.options = Array.isArray(game.options)
    ? game.options
    : [];
  const blanks: typeof game.blanks = Array.isArray(game.blanks)
    ? game.blanks
    : [];

  // Cantidad de huecos {} presentes en el texto. Define cuántos números de
  // espacio deberían asignarse en el banco de opciones.
  const blankCountInText = (game.text ?? "").split("{}").length - 1;

  // El número de espacio que el autor ve junto a cada opción es 1-indexed.
  // Internamente cada hueco {} del texto se identifica por su orden de
  // aparición (0,1,2...), que en el shape persistido corresponde al ÍNDICE del
  // blank dentro del array `blanks` (así lo consume el frontend). Por eso aquí
  // mantenemos `blanks` ordenado por posición y derivado de la opción asignada.

  // Devuelve el número de espacio (1-indexed) que llena una opción, o null si
  // la opción es un distractor (no asignada a ningún hueco).
  const blankNumberForOption = (optionId: string): number | null => {
    const idx = blanks.findIndex((b) => b.correctOptionId === optionId);
    return idx === -1 ? null : idx + 1;
  };

  // Asigna (o quita) el número de espacio de una opción. Reconstruye el array
  // `blanks` ordenado por número, reutilizando ids previos cuando es posible.
  const setOptionBlankNumber = (optionId: string, num: number | null) => {
    // Mapa número(1-indexed) -> optionId, partiendo del estado actual.
    const assignment = new Map<number, string>();
    blanks.forEach((b, idx) => {
      if (b.correctOptionId) assignment.set(idx + 1, b.correctOptionId);
    });
    // Quitar cualquier asignación previa de esta opción.
    for (const [n, oid] of Array.from(assignment.entries())) {
      if (oid === optionId) assignment.delete(n);
    }
    if (num !== null) assignment.set(num, optionId);

    // Reconstruir blanks ordenados por número. Conservamos ids existentes por
    // posición para no invalidar referencias innecesariamente.
    const sortedNums = Array.from(assignment.keys()).sort((a, b) => a - b);
    const newBlanks = sortedNums.map((n, idx) => ({
      id: blanks[idx]?.id ?? generateOptionId(),
      position: idx,
      correctOptionId: assignment.get(n)!,
    }));

    onChange({ ...game, blanks: newBlanks });
  };

  const updateOption = (
    optionId: string,
    patch: Partial<(typeof game.options)[number]>,
  ) => {
    onChange({
      ...game,
      options: options.map((o) =>
        o.id === optionId ? { ...o, ...patch } : o,
      ),
    });
  };

  const addOption = () =>
    onChange({
      ...game,
      options: [...options, { id: generateOptionId(), text: "" }],
    });

  const removeOption = (optionId: string) => {
    // Al borrar la opción quitamos el hueco que llenaba (si lo tenía) y
    // re-indexamos las posiciones para no dejar espacios fantasma.
    const newBlanks = blanks
      .filter((b) => b.correctOptionId !== optionId)
      .map((b, idx) => ({ ...b, position: idx }));
    onChange({
      ...game,
      options: options.filter((o) => o.id !== optionId),
      blanks: newBlanks,
    });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>
      <div>
        <label>Imagen (opcional)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url })}
        />
      </div>
      <div>
        <label>
          Texto con espacios (usa <code>{"{}"}</code> para marcar los huecos)
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.text}
            onChange={(e) => onChange({ ...game, text: e.target.value })}
            rows={3}
            placeholder="Ej: El gato {} sobre la {}."
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.text}
            audioUrl={game.textAudio}
            onAudioChange={(url) => onChange({ ...game, textAudio: url })}
            generateAudio={generateAudioWithBlanks}
          />
        </div>
        <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>
          {blankCountInText === 0
            ? "Aún no hay huecos {} en el texto."
            : `Huecos detectados: ${blankCountInText}. Numerá las opciones del 1 al ${blankCountInText}.`}
        </div>
      </div>
      <div>
        <label>Banco de opciones (mostradas al estudiante)</label>
        <div style={{ color: "#888", fontSize: 12, marginTop: 2 }}>
          Asigná el número de espacio <code>{"{}"}</code> que llena cada opción
          (1 = primer hueco). Dejá vacío el número en las opciones que sean
          distractores.
        </div>
        {options.map((option) => (
          <div
            key={option.id}
            style={{
              marginTop: 8,
              padding: 8,
              border: "1px solid var(--bo-card-border)",
              borderRadius: 6,
            }}
          >
            <Space style={{ display: "flex", alignItems: "flex-start" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: 4,
                }}
              >
                <span style={{ fontSize: 12, color: "#555" }}>Espacio</span>
                <InputNumber
                  min={1}
                  max={blankCountInText || options.length}
                  value={blankNumberForOption(option.id)}
                  onChange={(v) =>
                    setOptionBlankNumber(
                      option.id,
                      v === null || v === undefined ? null : Number(v),
                    )
                  }
                  placeholder="—"
                  style={{ width: 70 }}
                />
              </div>
              <OptionRichText
                value={option.text}
                onChange={(html) => updateOption(option.id, { text: html })}
                placeholder="Texto de la opción"
              />
              <TtsAudioControls
                text={stripHtml(option.text)}
                audioUrl={option.audio}
                onAudioChange={(url) => updateOption(option.id, { audio: url })}
                generateAudio={generateAudio}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeOption(option.id)}
              />
            </Space>
            <div style={{ marginTop: 8 }}>
              <ImageUploadInput
                value={option.image}
                onChange={(url) => updateOption(option.id, { image: url })}
                label="Imagen de la opción"
              />
            </div>
          </div>
        ))}
        <div style={{ marginTop: 8 }}>
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            block
            onClick={addOption}
          >
            Agregar opción
          </Button>
        </div>
      </div>
    </Space>
  );
};

export default FillInBlanksEditor;
